/**
 * AccountRateOverrideDialog — create or edit ONE negotiated rate on an Account's Rate
 * Card (RP-12.4). One dialog serves both, per the LaborCategoryEditorDialog convention.
 *
 * Each override is its own row with its own effective dates and reason — this is NOT the
 * firm-wide Rate Card's whole-card draft (that card's Avg Project Mix % must total
 * exactly 100%, a cross-row invariant this Account-scoped surface does not share), so
 * saving here touches exactly one override.
 *
 * TERMINOLOGY (§3): "Labor Category" never "Role"; "Rate Basis" never "price list".
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  type AccountRateCardView,
  type AccountRateOverrideView,
  useCreateAccountRateOverride,
  useUpdateAccountRateOverride,
} from "@/hooks/use-account-rate-overrides";

/** Radix SelectItem forbids an empty-string value — this sentinel maps back to null,
 * meaning the override applies to every Rate Basis. */
const EVERY_BASIS = "__every_basis__";

/** A decimal(x,2) — the same shape RateCardManager's NumericCell accepts. */
const DECIMAL2 = /^\d+(\.\d{1,2})?$/;

const schema = z
  .object({
    billRate: z.string().trim().regex(DECIMAL2, "Enter a rate like 185.00"),
    rateBasisId: z.string(),
    effectiveStart: z.string().min(1, "Effective start is required"),
    effectiveEnd: z.string(),
    reason: z.string().trim().min(1, "Reason is required").max(500),
    sourceDocumentReference: z.string().trim().max(200),
  })
  .refine((v) => v.effectiveEnd === "" || v.effectiveEnd >= v.effectiveStart, {
    message: "Effective end cannot be before effective start",
    path: ["effectiveEnd"],
  });

type FormValues = z.infer<typeof schema>;

/** A `<input type="date">` value ("YYYY-MM-DD") to a UTC-midnight ISO 8601 timestamp. */
function toIsoMidnight(dateOnly: string): string {
  return new Date(`${dateOnly}T00:00:00.000Z`).toISOString();
}

function todayDateOnly(): string {
  return new Date().toISOString().slice(0, 10);
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  laborCategory: { id: string; name: string };
  bases: AccountRateCardView["bases"];
  /** null when negotiating a new rate; otherwise editing the currently applied override. */
  override: AccountRateOverrideView | null;
}

export function AccountRateOverrideDialog({
  open,
  onOpenChange,
  accountId,
  laborCategory,
  bases,
  override,
}: Props) {
  const isEdit = override !== null;
  const create = useCreateAccountRateOverride(accountId);
  const update = useUpdateAccountRateOverride(accountId);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      billRate: "",
      rateBasisId: EVERY_BASIS,
      effectiveStart: todayDateOnly(),
      effectiveEnd: "",
      reason: "",
      sourceDocumentReference: "",
    },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({
      billRate: override?.billRate ?? "",
      rateBasisId: override?.rateBasisId ?? EVERY_BASIS,
      effectiveStart: override?.effectiveStart.slice(0, 10) ?? todayDateOnly(),
      effectiveEnd: override?.effectiveEnd?.slice(0, 10) ?? "",
      reason: override?.reason ?? "",
      sourceDocumentReference: override?.sourceDocumentReference ?? "",
    });
  }, [open, override, form]);

  async function onSubmit(values: FormValues) {
    const payload = {
      rateBasisId: values.rateBasisId === EVERY_BASIS ? null : values.rateBasisId,
      billRate: values.billRate,
      effectiveStart: toIsoMidnight(values.effectiveStart),
      effectiveEnd: values.effectiveEnd ? toIsoMidnight(values.effectiveEnd) : null,
      reason: values.reason,
      sourceDocumentReference: values.sourceDocumentReference.trim() || null,
    };
    try {
      if (override) {
        await update.mutateAsync({ overrideId: override.id, ...payload });
      } else {
        await create.mutateAsync({ laborCategoryId: laborCategory.id, ...payload });
      }
      toast.success(`Negotiated rate saved for ${laborCategory.name}.`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save the negotiated rate.");
    }
  }

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit negotiated rate" : "Add negotiated rate"} — {laborCategory.name}
          </DialogTitle>
          <DialogDescription>
            This overrides the standard Rate Card for this Account only. Leave the Rate Basis on
            "Every basis" unless this Account was quoted a different rate per basis.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="billRate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Negotiated rate</FormLabel>
                    <FormControl>
                      <Input inputMode="decimal" placeholder="185.00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="rateBasisId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rate Basis</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={EVERY_BASIS}>Every basis</SelectItem>
                        {bases.map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.name}
                            {b.active ? "" : " (inactive)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="effectiveStart"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Effective from</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="effectiveEnd"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Effective to</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormDescription>
                      Optional — leave blank for an open-ended rate.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={2}
                      placeholder="Why this Account negotiated a different rate"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sourceDocumentReference"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Source document reference</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional — SOW or amendment reference" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? "Saving…" : isEdit ? "Save Changes" : "Add rate"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
